import React from 'react';
import { MapPin, Phone, Mail, Send, Clock } from 'lucide-react';
import { useContactForm } from '../hooks/useContactForm';
import { companyInfo } from '../data/mockData';

const Contact = () => {
  const { formData, isSubmitting, feedback, handleChange, handleSubmit } = useContactForm();

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 data-testid="contact-page-title" className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
              Contactez-nous
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Une question ? Un projet ? N'hésitez pas à nous contacter. Nous sommes à votre écoute.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Demande de devis</h2>
              <form data-testid="contact-form" aria-busy={isSubmitting} onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    data-testid="contact-name-input"
                    autoComplete="name"
                    minLength={2}
                    maxLength={150}
                    readOnly={isSubmitting}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    data-testid="contact-email-input"
                    autoComplete="email"
                    readOnly={isSubmitting}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    data-testid="contact-phone-input"
                    autoComplete="tel"
                    maxLength={40}
                    readOnly={isSubmitting}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="06 XX XX XX XX"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service souhaité *
                  </label>
                  <select
                    id="service"
                    data-testid="contact-service-select"
                    disabled={isSubmitting}
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="conception">Conception</option>
                    <option value="creation">Création</option>
                    <option value="entretien">Entretien</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-message-input"
                    minLength={10}
                    maxLength={5000}
                    readOnly={isSubmitting}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors resize-none"
                    placeholder="Décrivez votre projet..."
                  />
                </div>

                {feedback && (
                  <p
                    data-testid={`contact-${feedback.type}-message`}
                    role={feedback.type === 'error' ? 'alert' : 'status'}
                    className={`rounded-lg border p-4 text-sm ${feedback.type === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`}
                  >
                    {feedback.message}
                  </p>
                )}
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send size={20} />
                      Envoyer la demande
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Nos coordonnées</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <MapPin className="text-green-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                    <p data-testid="contact-address" className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <Phone className="text-green-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                    <a data-testid="contact-phone-link" href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-green-700 transition-colors">{companyInfo.phone}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <Mail className="text-green-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a data-testid="contact-email-link" href={`mailto:${companyInfo.email}`} className="text-gray-600 break-all hover:text-green-700 transition-colors">{companyInfo.email}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <Clock className="text-green-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Horaires</h3>
                    <p data-testid="contact-weekday-hours" className="text-gray-600">Lundi - Vendredi : 8h00 - 18h00</p>
                    <p data-testid="contact-saturday-hours" className="text-gray-600">Samedi : 8h00 - 12h00</p>
                  </div>
                </div>
              </div>

              <a
                data-testid="contact-directions-link"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-700 font-semibold hover:text-green-900 transition-colors"
              >
                <MapPin size={20} /> Voir l’itinéraire (Google Maps)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-12 px-4 bg-gradient-to-br from-green-600 to-green-700">
        <div className="container mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4 text-white">
            Devis gratuit et sans engagement
          </h3>
          <p className="text-green-100 max-w-2xl mx-auto">
            Nous étudions votre demande et vous proposons une solution adaptée à vos besoins et votre budget. Délai de réponse : 48h maximum.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;